import { useState } from 'react'
import { useLocation, useOutletContext } from 'react-router-dom'

import { LOCAL_STORAGE_KEYS_FACTORIAL_TIPS } from 'config/Vars'
import { Features } from 'generated/featureDefinitions'
import { ProjectManagementProjectListPageDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import { SalesProductModal } from 'modules/finance/components/upsell/SalesProductModal'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { AnnouncementModal } from 'modules/project_management/components/AnnouncementModal'
import ProjectsDataCollection from 'modules/project_management/components/tables/ProjectsDataCollection'
import UpsellProjectsModal from 'modules/project_management/components/upsells/UpsellProjectsModal'

const ProjectsListPage = () => {
  const location = useLocation()
  const [crmUpsellBannerIsHidden, setCrmUpsellBannerIsHidden] = useLocalStorage<boolean>(
    LOCAL_STORAGE_KEYS_FACTORIAL_TIPS.hideProjectsCrmUpsellBannerOnImportProject
  )
  const [showCrmUpsellModal, setShowCrmUpsellModal] = useState(
    () => !!location.state?.showCrmUpsellAfterImport && !crmUpsellBannerIsHidden
  )

  useEffect(() => {
    track(trackingEvents.projectManagement.projects.goToProjectSection)
  }, [])

  useEffect(() => {
    if (location.state?.showCrmUpsellAfterImport && !crmUpsellBannerIsHidden) {
      setShowCrmUpsellModal(true)
      window.history.replaceState({}, '')
    }
  }, [location.state, crmUpsellBannerIsHidden])

  const { company } = useCurrentSession()
  const { hasExpenses } = useOutletContext<{ hasExpenses: boolean }>()

  const { data, loading, error } = useQuery(ProjectManagementProjectListPageDocument, {
    variables: {
      companyId: company.id,
      featureIds: [
        Features.projects_bulk_actions,
        Features.projects_clients,
        Features.projects_costs,
        Features.projects_flexible_tracking,
        Features.projects_import,
        Features.projects_with_subprojects,
        Features.projects_tasks,
      ],
    },
  })

  if (!data) return null

  const hasBulkActions = !!data.companies.featureAvailabilitiesConnection.nodes.find(
    (feature) => feature.id === Features.projects_bulk_actions
  )?.available
  const hasCostsFeature = !!data.companies.featureAvailabilitiesConnection.nodes.find(
    (feature) => feature.id === Features.projects_costs
  )?.available
  const hasFlexibleTracking = !!data.companies.featureAvailabilitiesConnection.nodes.find(
    (feature) => feature.id === Features.projects_flexible_tracking
  )?.available
  const hasImportProjectsEnabled = !!data.companies.featureAvailabilitiesConnection.nodes.find(
    (feature) => feature.id === Features.projects_import
  )?.available
  const clientsFeatureAvailable = !!data.companies.featureAvailabilitiesConnection.nodes.find(
    (feature) => feature.id === Features.projects_clients
  )?.available
  const hasSubprojectsFeature = !!data.companies.featureAvailabilitiesConnection.nodes.find(
    (feature) => feature.id === Features.projects_with_subprojects
  )?.available
  const hasProjectTasksFeature = !!data.companies.featureAvailabilitiesConnection.nodes.find(
    (feature) => feature.id === Features.projects_tasks
  )?.available

  const projectPolicies = data.projectManagement._policies

  const canManageProjects = projectPolicies.projectManage.can
  const canCreateProjects = projectPolicies.projectCreate.can
  const canDeleteProjects = projectPolicies.projectSoftDelete.can
  const canSeeCosts = projectPolicies.projectSeeCost.can

  const showUpsellScheduleModal = !clientsFeatureAvailable // We'll unify all FFs eventually, so far I need a FF I know will be active at the same time as the schedule (so same plan)
  const showUpsellKanbanTasksModal = !hasProjectTasksFeature
  const showUpsellModal = showUpsellScheduleModal || showUpsellKanbanTasksModal

  const hasTimeTrackingSection =
    data.attendance._policies.sidebarSeeAttendanceCompanyTopLevelItem.can

  return (
    <ResponsivePage.Body variant="wide" loading={loading} error={error}>
      <SalesProductModal
        isOpen={showCrmUpsellModal}
        onClose={() => {
          setShowCrmUpsellModal(false)
          setCrmUpsellBannerIsHidden(true)
        }}
        requestSource="sales_from_projects_list_on_import_project"
        pageSource={location.pathname}
      />
      <UpsellProjectsModal
        showUpsellModal={showUpsellModal}
        scheduleUpsell={showUpsellScheduleModal}
        kanbanTasksUpsell={showUpsellKanbanTasksModal}
      />

      {clientsFeatureAvailable || (hasProjectTasksFeature && <AnnouncementModal />)}

      <ProjectsDataCollection
        canSeeCosts={canSeeCosts}
        canCreateProjects={canCreateProjects}
        canDeleteProjects={canDeleteProjects}
        canManageProjects={canManageProjects}
        hasBulkActionsFeature={hasBulkActions}
        hasClientsFeature={clientsFeatureAvailable}
        hasCostsFeature={hasCostsFeature}
        hasExpensesFeature={hasExpenses}
        hasFlexibleTrackingFeature={hasFlexibleTracking}
        hasImportFeature={hasImportProjectsEnabled}
        hasSubprojectsFeature={hasSubprojectsFeature}
        hasTimeTrackingSection={hasTimeTrackingSection}
      />
    </ResponsivePage.Body>
  )
}

export default ProjectsListPage
